/**
 * DonationDetailModal.tsx - Donation Details Display Modal
 *
 * This component displays comprehensive details about individual donations
 * including donor information, payment details, and transaction metadata.
 *
 * Key Features:
 * - Complete donation information display
 * - Donor contact details and preferences
 * - Payment method and transaction information
 * - Donation comments and messages
 * - Timestamp and processing details
 * - Refund and management options
 *
 * Information Displayed:
 * - Donation amount and currency
 * - Donor name and contact information
 * - Payment method (card/ACH) with masked details
 * - Transaction timestamp and ID
 * - Donation comments or messages
 * - Anonymous donation handling
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - donation: Donation | null - Donation to display
 *
 * Dependencies: Donation types, MobX store, portal rendering
 */

import React from "react";
import { createPortal } from "react-dom";
import { X, Heart } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import styled from "styled-components";
import { FundraisersDonationsBoardResponse } from "../models";
import { AuthService } from "@mindmemobile/mm-shared-global";
import { RenderPaymentMethod } from "@mindmemobile/mm-payment-modules";
import { IUpdateDonationRequestModel } from "../interfaces";
import { FundraiserService } from "../services";
import { firstValueFrom } from "rxjs";
import ToastService from "./ToastService";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 650px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalTitleBar = styled.div`
  background: #effdf4;
  padding: 32px 32px 14px;
  text-align: center;
  border-radius: 24px 24px 0 0;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #10b981;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const DonationAmount = styled.div`
  font-size: 36px;
  font-weight: 800;
  color: #000000;
  margin: 16px 0 0 0;
  text-align: center;
`;

const ContentContainer = styled.div`
  padding: 24px 40px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 12px 62px;
  margin-bottom: 20px;
  align-items: start;
`;

const DetailLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  text-align: left;
`;

const DetailValue = styled.div`
  font-size: 16px;
  color: #1e293b;
  font-weight: 500;
  line-height: 1.4;
  text-align: left;
`;

const AddressValue = styled.div`
  font-size: 16px;
  color: #1e293b;
  font-weight: 500;
  line-height: 1.5;
  text-align: left;
  white-space: pre-line;
`;

const PaymentMethodContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaymentMethodIcon = styled.div`
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  padding: 2px;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
`;

const CommentTextArea = styled.textarea`
  width: 100%;
  min-height: 50px;
  padding: 12px 16px 50px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  resize: none;
  font-family: inherit;
  transition: border-color 0.2s ease;
  background: #ffffff;
  overflow-y: hidden;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CommentSection = styled.div`
  margin-bottom: 16px;
`;

const CommentTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const CommentContainer = styled.div`
  position: relative;
`;

const CheckboxSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0 25px 0;
`;

const EmojiFooter = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  gap: 4px;
  pointer-events: none;
`;

const EmojiOptions = styled.div<{ visible: boolean }>`
  display: flex;
  gap: 4px;
  margin-right: 8px;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transform: translateX(${(props) => (props.visible ? "0" : "20px")});
  transition: all 0.3s ease;
  animation: ${(props) =>
    props.visible ? "slideInLeft 0.3s ease-out" : "none"};
  pointer-events: auto;

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const EmojiOption = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  pointer-events: auto;

  &:hover {
    background: #f1f5f9;
    transform: scale(1.32);
  }
`;

const EmojiButton = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  filter: hue-rotate(15deg) saturate(1.2) brightness(1.1);
  pointer-events: auto;

  &:hover {
    background: #f1f5f9;
    transform: scale(1.2);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 16px;
  color: #1e293b;
  font-weight: 500;
`;

const Checkbox = styled.input<{ checked?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid ${(props) => (props.checked ? "#12B981" : "#e2e8f0")};
  background: ${(props) => (props.checked ? "#12B981" : "#ffffff")};
  appearance: none;
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:checked::after {
    content: "✓";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
  }

  &:hover {
    border-color: #12b981;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 0px 40px 32px;
  margin-top: -22px;
`;

const ModalButton = styled.button<{ variant?: "primary" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #12B981;
    color: #ffffff;

    &:hover {
      background: #059669;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

const additionalEmojis = [
  "❤️",
  "🙏",
  "😢",
  "💕",
  "👏",
  "🎉",
  "😇",
  "💪",
  "⭐",
  "🌎",
  "🐶",
  "🫶",
  "👍",
  "🐱",
  "🌱",
];

interface DonationDetailModalProps {
  isVisible: boolean;
  donation: FundraisersDonationsBoardResponse | null;
  onClose: () => void;
}

export const DonationDetailModal: React.FC<DonationDetailModalProps> = ({
  isVisible,
  donation,
  onClose,
}) => {
  const [comment, setComment] = React.useState("");
  const [isAnonymous, setIsAnonymous] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [showAdditionalEmojis, setShowAdditionalEmojis] = React.useState(false);
  const [initialData, setInitialData] = React.useState({
    comment: "",
    isAnonymous: false,
  });
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    const newHeight = Math.max(50, textarea.scrollHeight);
    textarea.style.height = newHeight + "px";
  };

  // Initialize form data when modal opens
  React.useEffect(() => {
    if (isVisible && donation) {
      const data = {
        comment: donation?.comment || "",
        isAnonymous: donation?.isAnonymous || false,
      };
      setComment(data.comment);
      setIsAnonymous(data.isAnonymous);
      setInitialData(data);
      setHasChanges(false);
      setShowAdditionalEmojis(false); // Reset emoji bar to closed state when modal opens

      // Auto-resize textarea for existing content after a short delay
      setTimeout(() => {
        if (textareaRef.current && data.comment) {
          autoResizeTextarea(textareaRef.current);
        }
      }, 100);
    }
  }, [isVisible, donation]);

  // Track changes
  React.useEffect(() => {
    const hasAnyChanges =
      comment !== initialData.comment ||
      isAnonymous !== initialData.isAnonymous;
    setHasChanges(hasAnyChanges);
  }, [comment, isAnonymous, initialData]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isVisible) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isVisible]);

  if (!donation) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const getPromoterName = (promoterId?: string) => {
    if (!promoterId) return "No Promoter";
    const promoter = fundraiserStore.promoters.find((p) => p.id === promoterId);
    return promoter?.displayName || "Unknown Promoter";
  };

  const getFormattedAddress = () => {
    // Mock address data - in real app this would come from donation
    return `958 Milkweed Lane\nHuntingdon Valley, PA 19006\nUnited States`;
  };

  const generateOrderId = () => {
    return `Ord_${donation?.donationId.slice(-6)}`;
  };

  const handleEmojiClick = (emoji: string) => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newComment =
        comment.substring(0, start) + emoji + comment.substring(end);
      setComment(newComment);

      // Set cursor position after the emoji and auto-resize
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
        autoResizeTextarea(textarea);
      }, 0);
    } else {
      setComment((prev) => prev + emoji);
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
    autoResizeTextarea(e.target);
  };

  const handlePrimaryEmojiClick = () => {
    if (!showAdditionalEmojis) {
      setShowAdditionalEmojis(true);
    } else {
      handleEmojiClick("😊");
    }
  };

  const handleUpdate = async () => {
    if (!hasChanges || !donation) return;
    // Update the donation in the store
    fundraiserStore.updateDonation(donation?.id, {
      comment: comment,
      isAnonymous: isAnonymous,
    });

    const requestData: IUpdateDonationRequestModel = {
      comments: comment,
      anonymousDonation: isAnonymous,
      id: donation.id,
      accountId: AuthService.authData.accountId,
    };

    const response = await firstValueFrom(
      FundraiserService.updateDonationDetails(requestData)
    );
    if (response.result.success) {
      ToastService.showSuccess("Donation details updated successfully.");
    } else {
      ToastService.showError("Failed to update donation details.");
    }
    setInitialData({ comment: comment, isAnonymous: isAnonymous });
    setHasChanges(false);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return createPortal(
    <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <X size={16} />
        </CloseButton>

        <ModalTitleBar>
          <ModalTitle>
            <Heart size={26} color="#10b981" />
            Donation Details
          </ModalTitle>
        </ModalTitleBar>

        <DonationAmount>{formatCurrency(donation?.amount)}</DonationAmount>

        <ContentContainer>
          <DetailsGrid>
            <DetailLabel>Name</DetailLabel>
            <DetailValue>{donation?.getDisplayName()}</DetailValue>
            {/* 
            <DetailLabel>Address</DetailLabel>
            <AddressValue>{getFormattedAddress()}</AddressValue> */}

            <DetailLabel>Email Address</DetailLabel>
            <DetailValue>{donation?.donorEmail || "–"}</DetailValue>

            <DetailLabel>Mobile Phone</DetailLabel>
            <DetailValue>
              {`+${donation?.donorMobile?.countryCode + " " + donation?.donorMobile?.phoneNumber || "–"}`}
            </DetailValue>
            {/* 
            <DetailLabel>Order</DetailLabel>
            <DetailValue>{generateOrderId()}</DetailValue> */}

            <DetailLabel>Date</DetailLabel>
            <DetailValue>{formatDate(donation?.donatedOn)}</DetailValue>

            <DetailLabel>Amount</DetailLabel>
            <DetailValue>{formatCurrency(donation?.amount)} USD</DetailValue>

            <DetailLabel>Frequency</DetailLabel>
            <DetailValue>
              {donation?.isMonthlyDonation ? "Monthly" : "One Time"}
            </DetailValue>

            <DetailLabel>Payment Method</DetailLabel>
            <DetailValue>
              <RenderPaymentMethod paymentMethod={donation.paymentMethod} />
            </DetailValue>

            <DetailLabel>Merchant Account</DetailLabel>
            <DetailValue style={{ marginBottom: "8px" }}>
              {donation?.merchantAccount?.name || "–"}
            </DetailValue>
          </DetailsGrid>

          <CommentSection>
            <CommentTitleRow>
              <DetailLabel>Comment</DetailLabel>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                />
                Anonymous Donation
              </CheckboxLabel>
            </CommentTitleRow>
            <CommentContainer>
              <CommentTextArea
                ref={textareaRef}
                id="comment-textarea"
                placeholder="Enter donation comment..."
                value={comment}
                onChange={handleCommentChange}
              />
              <EmojiFooter>
                <EmojiOptions visible={showAdditionalEmojis}>
                  {additionalEmojis.map((emoji, index) => (
                    <EmojiOption
                      key={index}
                      type="button"
                      onClick={() => handleEmojiClick(emoji)}
                    >
                      {emoji}
                    </EmojiOption>
                  ))}
                </EmojiOptions>
                <EmojiButton type="button" onClick={handlePrimaryEmojiClick}>
                  😊
                </EmojiButton>
              </EmojiFooter>
            </CommentContainer>
          </CommentSection>
        </ContentContainer>

        <ModalActions>
          <ModalButton onClick={handleCancel}>Cancel</ModalButton>
          <ModalButton
            variant="primary"
            onClick={handleUpdate}
            disabled={!hasChanges}
          >
            Update
          </ModalButton>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
